import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck, ShieldAlert, Gauge, FileCheck, MapPin, Loader2,
  RefreshCw, Plus, Trash2, AlertTriangle, Save, ChevronDown, X, Check,
} from "lucide-react";

const PRODUCT_LABELS = {
  education: "Education Fees",
  bbps: "BBPS",
  topup: "Top-up",
  payout: "Payouts",
  wallet: "Wallet",
};

const BBPS_SUBCATEGORIES = [
  { id: "credit-card", label: "Credit Card", type: "Financial" },
  { id: "loan-emi", label: "Loan EMI", type: "Financial" },
  { id: "insurance", label: "Insurance", type: "Financial" },
  { id: "electricity", label: "Electricity", type: "Utilities" },
  { id: "gas", label: "Gas", type: "Utilities" },
  { id: "water", label: "Water", type: "Utilities" },
  { id: "mobile-postpaid", label: "Mobile Postpaid", type: "Telecom" },
  { id: "mobile-prepaid", label: "Mobile Prepaid", type: "Telecom" },
  { id: "dth", label: "DTH", type: "Telecom" },
  { id: "broadband", label: "Broadband", type: "Telecom" },
  { id: "fastag", label: "FASTag", type: "Transport" },
];
const SUBCATS_BY_PRODUCT = {
  bbps: BBPS_SUBCATEGORIES,
};

const CATEGORY_META = {
  identity: { label: "Identity & Card Security", icon: ShieldCheck, color: "text-blue-700" },
  velocity: { label: "Usage & Velocity Limits", icon: Gauge, color: "text-purple-700" },
  risk_based: { label: "Risk-Based Controls", icon: ShieldAlert, color: "text-amber-700" },
  consent: { label: "Consent & Onboarding", icon: FileCheck, color: "text-emerald-700" },
};

const ACTIONS = ["block", "manual_review", "warn", "allow"];
const WINDOW_OPTIONS = [
  { value: "calendar_day", label: "Calendar day (IST midnight)" },
  { value: "rolling_24h", label: "Rolling 24 hours" },
];
const MONTH_WINDOW_OPTIONS = [
  { value: "calendar_month", label: "Calendar month" },
  { value: "rolling_30d", label: "Rolling 30 days" },
];

// Minimal Indian state list — admins can add more via the per-state thresholds panel.
const INDIA_STATES = [
  ["IN-AP", "Andhra Pradesh"], ["IN-AR", "Arunachal Pradesh"], ["IN-AS", "Assam"],
  ["IN-BR", "Bihar"], ["IN-CT", "Chhattisgarh"], ["IN-GA", "Goa"], ["IN-GJ", "Gujarat"],
  ["IN-HR", "Haryana"], ["IN-HP", "Himachal Pradesh"], ["IN-JK", "Jammu & Kashmir"],
  ["IN-JH", "Jharkhand"], ["IN-KA", "Karnataka"], ["IN-KL", "Kerala"], ["IN-MP", "Madhya Pradesh"],
  ["IN-MH", "Maharashtra"], ["IN-MN", "Manipur"], ["IN-ML", "Meghalaya"], ["IN-MZ", "Mizoram"],
  ["IN-NL", "Nagaland"], ["IN-OD", "Odisha"], ["IN-PB", "Punjab"], ["IN-RJ", "Rajasthan"],
  ["IN-SK", "Sikkim"], ["IN-TN", "Tamil Nadu"], ["IN-TG", "Telangana"], ["IN-TR", "Tripura"],
  ["IN-UP", "Uttar Pradesh"], ["IN-UK", "Uttarakhand"], ["IN-WB", "West Bengal"], ["IN-DL", "Delhi"],
];

export default function AdminRiskRules() {
  const [product, setProduct] = useState("education");
  const [subcategories, setSubcategories] = useState([]);  // [] = product default only
  const [rules, setRules] = useState([]);  // overrides ONLY when subcategories.length >= 1; else defaults
  const [defaultRules, setDefaultRules] = useState([]);  // always product defaults
  const [overridesByCat, setOverridesByCat] = useState({});  // { sc_id: [rules] } when multi-select
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch defaults + states
      const [{ data: def }, { data: stData }] = await Promise.all([
        api.get(`/admin/risk-rules?product=${product}&subcategory=default`),
        api.get(`/admin/risk-rules/states?product=${product}`),
      ]);
      setDefaultRules(def.rules || []);
      setStates(stData.states || []);

      if (subcategories.length === 0) {
        setRules(def.rules || []);
        setOverridesByCat({});
      } else if (subcategories.length === 1) {
        const sc = subcategories[0];
        const { data: r } = await api.get(`/admin/risk-rules?product=${product}&subcategory=${encodeURIComponent(sc)}`);
        setRules(r.rules || []);
        setOverridesByCat({ [sc]: r.rules || [] });
      } else {
        // Multi-select: fetch overrides for each, render product-defaults with a
        // "applies to N sub-categories" hint.
        const out = {};
        await Promise.all(subcategories.map(async (sc) => {
          const { data: r } = await api.get(`/admin/risk-rules?product=${product}&subcategory=${encodeURIComponent(sc)}`);
          out[sc] = r.rules || [];
        }));
        setOverridesByCat(out);
        setRules([]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load risk rules");
    } finally { setLoading(false); }
  }, [product, subcategories]);

  useEffect(() => { load(); }, [load]);

  // When product changes, reset sub-categories
  useEffect(() => { setSubcategories([]); }, [product]);

  // Effective rules:
  // - 0 selected: rules = defaults (no inherited badge)
  // - 1 selected: overrides + inherited defaults
  // - 2+ selected: always show defaults; per-row badge shows N/total overrides
  const effective = useMemo(() => {
    if (subcategories.length === 0) return defaultRules;
    if (subcategories.length === 1) {
      const overrideKeys = new Set(rules.map((r) => r.key));
      const inherited = defaultRules.filter((r) => !overrideKeys.has(r.key))
        .map((r) => ({ ...r, _inherited: true }));
      return [...rules, ...inherited];
    }
    // 2+: present defaults; multi-save fans out
    return defaultRules.map((r) => ({ ...r, _multi: true }));
  }, [rules, defaultRules, subcategories]);

  const grouped = useMemo(() => {
    const out = { identity: [], velocity: [], risk_based: [], consent: [] };
    for (const r of effective) (out[r.category] = out[r.category] || []).push(r);
    return out;
  }, [effective]);

  const onReseed = async () => {
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/risk-rules/seed");
      toast.success(`Seed complete · ${data.created || 0} rules inserted`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Seed failed");
    } finally { setReseeding(false); }
  };

  return (
    <div className="space-y-6" data-testid="risk-rules-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="overline mb-1 text-muted-foreground">Admin · Marketing & Analytics</p>
          <h1 className="text-2xl font-bold">Risk Rules Engine</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Configurable guardrails for every product. Toggle individual rules, change actions
            (block / manual review / warn) and set per-state thresholds. Every transaction is
            evaluated through this engine before settlement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="risk-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={onReseed} disabled={reseeding} data-testid="risk-seed">
            {reseeding ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            Seed defaults
          </Button>
        </div>
      </div>

      <Tabs value={product} onValueChange={setProduct} data-testid="risk-product-tabs">
        <TabsList>
          {Object.entries(PRODUCT_LABELS).map(([k, lbl]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-product-${k}`}>{lbl}</TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(PRODUCT_LABELS).map((p) => (
          <TabsContent key={p} value={p} className="space-y-6 mt-6">
            {SUBCATS_BY_PRODUCT[p] && (
              <SubcategoryMultiSelect
                product={p}
                value={subcategories}
                onChange={setSubcategories}
                options={SUBCATS_BY_PRODUCT[p]}
                overridesByCat={overridesByCat}
              />
            )}
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…
              </div>
            ) : (
              <>
                {Object.keys(CATEGORY_META).map((cat) => (
                  <CategoryCard
                    key={cat} category={cat}
                    rules={grouped[cat] || []} onUpdated={load}
                    productScope={{ product, subcategories, overridesByCat }}
                  />
                ))}
                <StateThresholdsCard product={p} states={states} onUpdated={load} />
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CategoryCard({ category, rules, onUpdated, productScope }) {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  return (
    <div className="surface-card p-5" data-testid={`category-${category}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${meta.color}`} />
        <h3 className="text-sm font-semibold">{meta.label}</h3>
      </div>
      {rules.length === 0 ? (
        <p className="text-xs text-muted-foreground">No rules configured. Click "Seed defaults" above.</p>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <RuleRow key={r.id || `${r.product}-${r.key}-${r.subcategory || "def"}`}
                     rule={r} onUpdated={onUpdated} productScope={productScope} />
          ))}
        </div>
      )}
    </div>
  );
}

function RuleRow({ rule, onUpdated, productScope }) {
  const subcategories = productScope?.subcategories || [];
  const overridesByCat = productScope?.overridesByCat || {};
  const isInherited = !!rule._inherited;
  const isMulti = subcategories.length > 1;
  const [enabled, setEnabled] = useState(rule.enabled);
  const [action, setAction] = useState(rule.action);
  const [value, setValue] = useState(rule.value || {});
  const [saving, setSaving] = useState(false);
  const dirty =
    enabled !== rule.enabled ||
    action !== rule.action ||
    JSON.stringify(value || {}) !== JSON.stringify(rule.value || {});

  // In multi-select view, count how many of the selected sub-categories
  // already have an override for THIS rule key — drives the badge.
  const overrideCount = useMemo(() => {
    if (!isMulti) return 0;
    let n = 0;
    for (const sc of subcategories) {
      const rows = overridesByCat[sc] || [];
      if (rows.some((r) => r.key === rule.key)) n++;
    }
    return n;
  }, [isMulti, subcategories, overridesByCat, rule.key]);

  const save = async () => {
    setSaving(true);
    try {
      if (subcategories.length === 0) {
        // Product-default scope — PATCH the existing row
        await api.patch(`/admin/risk-rules/${rule.id}`, { enabled, action, value });
        toast.success(`${rule.label} updated`);
      } else {
        // One or many sub-categories — fan-out via the multi-target endpoint
        const { data } = await api.post(`/admin/risk-rules/override`, {
          product: productScope.product,
          subcategories,
          rule_key: rule.key,
          enabled, value, action,
        });
        toast.success(`${rule.label} · applied to ${data.applied_to} sub-categor${data.applied_to === 1 ? "y" : "ies"}`);
      }
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const removeOverride = async () => {
    if (!rule.id || !rule.subcategory) return;
    if (!window.confirm(`Remove ${rule.label} override and fall back to product default?`)) return;
    try {
      await api.delete(`/admin/risk-rules/${rule.id}`);
      toast.success("Override removed");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="flex items-start gap-4 border-t border-border/50 pt-3 first:border-t-0 first:pt-0">
      <div className="pt-1">
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`switch-${rule.key}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-sm">{rule.label}</div>
          <Badge variant="outline" className="text-[10px]">{rule.key}</Badge>
          {isMulti && (
            <Badge variant="outline" className="text-[10px] text-violet-700 border-violet-400 bg-violet-50">
              {overrideCount > 0
                ? `${overrideCount}/${subcategories.length} sub-categor${subcategories.length === 1 ? "y" : "ies"} overridden`
                : "applies to selected sub-categories"}
            </Badge>
          )}
          {!isMulti && isInherited && (
            <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-400 bg-blue-50">
              inherited from product default
            </Badge>
          )}
          {!isMulti && !isInherited && rule.subcategory && (
            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-500 bg-amber-50">
              sub-category override
            </Badge>
          )}
          {!enabled && <Badge variant="outline" className="text-[10px] text-muted-foreground">disabled</Badge>}
        </div>
        {rule.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
        )}
        <RuleValueEditor rule={rule} value={value} onChange={setValue} />
      </div>
      <div className="flex flex-col items-end gap-1 w-[180px]">
        <select
          value={action} onChange={(e) => setAction(e.target.value)}
          className="text-xs rounded-md border border-input bg-background px-2 py-1.5 w-full"
          data-testid={`action-${rule.key}`}
        >
          {ACTIONS.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
        </select>
        <Button size="sm" disabled={!dirty || saving} onClick={save} data-testid={`save-${rule.key}`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          {isMulti ? `Apply to ${subcategories.length}` : (subcategories.length === 1 && isInherited ? "Override" : "Save")}
        </Button>
        {!isMulti && !isInherited && rule.subcategory && (
          <Button size="sm" variant="ghost" className="text-[10px] h-6 text-muted-foreground hover:text-rose-700"
                  onClick={removeOverride} data-testid={`remove-override-${rule.key}`}>
            <Trash2 className="h-3 w-3 mr-1" />Remove override
          </Button>
        )}
      </div>
    </div>
  );
}

function SubcategoryMultiSelect({ product, value, onChange, options, overridesByCat }) {
  const [open, setOpen] = useState(false);

  // Group by 'type' for nicer search results
  const groups = useMemo(() => {
    const out = {};
    for (const o of options) (out[o.type || "Other"] = out[o.type || "Other"] || []).push(o);
    return out;
  }, [options]);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(options.map((o) => o.id));

  const labelById = (id) => options.find((o) => o.id === id)?.label || id;
  const overrideCountFor = (id) => (overridesByCat?.[id] || []).length;

  return (
    <div className="surface-card p-4 flex flex-col gap-3" data-testid="subcategory-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sub-categories</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-w-[280px] justify-between"
              data-testid="subcategory-multiselect-trigger"
            >
              <span className="truncate text-left">
                {value.length === 0
                  ? "Default — applies to all sub-categories"
                  : value.length === 1
                    ? labelById(value[0])
                    : `${value.length} sub-categories selected`}
              </span>
              <ChevronDown className="h-3.5 w-3.5 ml-2 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[360px]" align="start">
            <Command>
              <CommandInput placeholder="Type to search (e.g. credit, dth, gas)…" data-testid="subcategory-search" />
              <CommandList className="max-h-[320px]">
                <CommandEmpty>No matching sub-category</CommandEmpty>
                {Object.entries(groups).map(([type, items]) => (
                  <CommandGroup key={type} heading={type}>
                    {items.map((o) => {
                      const selected = value.includes(o.id);
                      const overrides = overrideCountFor(o.id);
                      return (
                        <CommandItem
                          key={o.id}
                          value={`${o.label} ${o.id} ${type}`}
                          onSelect={() => toggle(o.id)}
                          data-testid={`subcat-option-${o.id}`}
                        >
                          <Checkbox checked={selected} className="mr-2 pointer-events-none" />
                          <span className="flex-1">{o.label}</span>
                          {overrides > 0 && (
                            <Badge variant="outline" className="text-[10px] ml-2 border-amber-500 text-amber-700 bg-amber-50">
                              {overrides} override{overrides > 1 ? "s" : ""}
                            </Badge>
                          )}
                          {selected && <Check className="h-3.5 w-3.5 ml-2 text-success" />}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
              <div className="flex items-center justify-between border-t border-border px-2 py-1.5 text-xs">
                <button onClick={selectAll} className="text-muted-foreground hover:text-foreground" data-testid="subcat-select-all">
                  Select all
                </button>
                <button onClick={clear} className="text-muted-foreground hover:text-rose-700" data-testid="subcat-clear">
                  Clear
                </button>
              </div>
            </Command>
          </PopoverContent>
        </Popover>
        {value.length > 0 && (
          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-500 bg-amber-50">
            Saves fan-out to {value.length} sub-categor{value.length === 1 ? "y" : "ies"}
          </Badge>
        )}
      </div>
      {value.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {value.map((id) => (
            <Badge
              key={id} variant="outline"
              className="text-[11px] gap-1 pl-2 pr-1 py-0.5 cursor-pointer hover:bg-accent"
              onClick={() => toggle(id)}
              data-testid={`chip-${id}`}
            >
              {labelById(id)}
              <X className="h-3 w-3 opacity-60" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleValueEditor({ rule, value, onChange }) {
  if (rule.key === "frequency_cap_day" || rule.key === "frequency_cap_month") {
    const opts = rule.key === "frequency_cap_day" ? WINDOW_OPTIONS : MONTH_WINDOW_OPTIONS;
    return (
      <div className="flex items-center gap-2 mt-2">
        <Label className="text-[11px] text-muted-foreground">Count</Label>
        <Input
          type="number" min={0} value={value.count ?? 0}
          onChange={(e) => onChange({ ...value, count: parseInt(e.target.value || "0", 10) })}
          className="w-20 h-8 text-sm" data-testid={`count-${rule.key}`}
        />
        <Label className="text-[11px] text-muted-foreground">Window</Label>
        <select
          value={value.window || opts[0].value}
          onChange={(e) => onChange({ ...value, window: e.target.value })}
          className="text-xs rounded-md border border-input bg-background px-2 py-1.5 flex-1"
          data-testid={`window-${rule.key}`}
        >
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  if (rule.key === "transaction_range") {
    return (
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <Label className="text-[11px] text-muted-foreground">Min ₹</Label>
        <Input
          type="number" min={0} value={value.min ?? ""}
          onChange={(e) => onChange({ ...value, min: parseFloat(e.target.value || "0") })}
          className="w-24 h-8 text-sm" data-testid={`min-${rule.key}`}
        />
        <Label className="text-[11px] text-muted-foreground">Max ₹</Label>
        <Input
          type="number" min={0} value={value.max ?? ""}
          onChange={(e) => onChange({ ...value, max: parseFloat(e.target.value || "0") })}
          className="w-28 h-8 text-sm" data-testid={`max-${rule.key}`}
        />
        <Label className="text-[11px] text-muted-foreground">Avg (hint) ₹</Label>
        <Input
          type="number" min={0} value={value.avg_hint ?? ""}
          onChange={(e) => onChange({ ...value, avg_hint: parseFloat(e.target.value || "0") })}
          className="w-24 h-8 text-sm" data-testid={`avg-${rule.key}`}
        />
      </div>
    );
  }
  // Boolean toggle rules: nothing extra to configure.
  return null;
}

function StateThresholdsCard({ product, states, onUpdated }) {
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const stateByCode = useMemo(() => {
    const m = {};
    for (const s of states) m[s.state_code] = s;
    return m;
  }, [states]);

  const upsert = async (code, name, patch) => {
    const cur = stateByCode[code] || {
      product, state_code: code, state_name: name, enabled: true,
      max_amount: null, requires_video_kyc: false, action: "manual_review",
    };
    const body = { ...cur, ...drafts[code], ...patch, product, state_code: code, state_name: name };
    setSaving((m) => ({ ...m, [code]: true }));
    try {
      await api.post("/admin/risk-rules/states", body);
      toast.success(`${name} updated`);
      setDrafts((d) => ({ ...d, [code]: {} }));
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "State update failed");
    } finally {
      setSaving((m) => ({ ...m, [code]: false }));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove high-risk threshold for ${name}?`)) return;
    try {
      await api.delete(`/admin/risk-rules/states/${id}`);
      toast.success(`${name} removed`);
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="surface-card p-5" data-testid="state-thresholds">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="h-4 w-4 text-rose-700" />
        <h3 className="text-sm font-semibold">High-Risk State Thresholds</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Toggle a state ON to flag it high-risk. Optional max amount and Video KYC requirement apply when the rule
        "High-Risk States — Video KYC" is enabled above. Worst action wins across rule + state.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {INDIA_STATES.map(([code, name]) => {
          const cur = stateByCode[code];
          const draft = drafts[code] || {};
          const isOn = cur ? !!cur.enabled : false;
          return (
            <div key={code} className="border border-border/60 rounded-md p-3" data-testid={`state-${code}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{code}</div>
                </div>
                <Switch
                  checked={isOn}
                  onCheckedChange={(v) => upsert(code, name, { enabled: v })}
                  data-testid={`state-toggle-${code}`}
                />
              </div>
              {isOn && cur && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] text-muted-foreground w-20">Max ₹</Label>
                    <Input
                      type="number" min={0}
                      defaultValue={cur.max_amount ?? ""}
                      onBlur={(e) => {
                        const newVal = e.target.value === "" ? null : parseFloat(e.target.value);
                        if (newVal !== cur.max_amount) upsert(code, name, { max_amount: newVal });
                      }}
                      className="h-7 text-xs" data-testid={`state-max-${code}`}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-[10px] text-muted-foreground">Video KYC</Label>
                    <Switch
                      checked={!!cur.requires_video_kyc}
                      onCheckedChange={(v) => upsert(code, name, { requires_video_kyc: v })}
                      data-testid={`state-vkyc-${code}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] text-muted-foreground w-20">Action</Label>
                    <select
                      defaultValue={cur.action}
                      onChange={(e) => upsert(code, name, { action: e.target.value })}
                      className="flex-1 text-xs rounded-md border border-input bg-background px-2 py-1"
                      data-testid={`state-action-${code}`}
                    >
                      {ACTIONS.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
                    </select>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="w-full h-7 text-xs text-muted-foreground hover:text-rose-700"
                    onClick={() => remove(cur.id, name)}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />Remove
                  </Button>
                </div>
              )}
              {saving[code] && (
                <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />saving…
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-[11px] text-muted-foreground flex items-start gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
        <span>
          Per-state thresholds combine with the global "High-Risk States — Video KYC" rule above.
          Disabling the global rule turns off all per-state Video KYC requirements regardless of toggles below.
        </span>
      </div>
    </div>
  );
}
